import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchListings } from "../lib/api";
import ListingCard from "../components/ListingCard";
import Seo from "../components/Seo";
import { Search, SlidersHorizontal } from "lucide-react";

const INDUSTRIES = ["All", "Technology", "Retail", "E-commerce", "Hospitality", "Food & Beverage", "Construction", "Manufacturing", "Healthcare", "Education", "Logistics", "Real Estate", "Agriculture", "Services", "Beauty & Wellness", "Automotive"];
const COUNTRIES = ["All", "UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt"];
const TYPES = [{ v: "", l: "All Types" }, { v: "sale", l: "For Sale" }, { v: "investment", l: "Seeking Investment" }, { v: "partnership", l: "Partnership" }];

export default function MarketplacePage() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState(params.get("industry") || "All");
  const [country, setCountry] = useState(params.get("country") || "All");
  const [type, setType] = useState(params.get("type") || "");
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (industry !== "All") p.industry = industry;
    if (country !== "All") p.country = country;
    if (type) p.listing_type = type;
    if (q) p.q = q;
    fetchListings(p).then(d => { setItems(d); setLoading(false); });
  }, [industry, country, type, q]);

  return (
    <div data-testid="marketplace-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <Seo
        title="Businesses For Sale & Investment Opportunities — DealVest Marketplace"
        description="Browse verified businesses for sale, investment opportunities, and partnerships across UAE, Saudi Arabia, Nigeria, Kenya, Egypt and more."
        path="/marketplace"
      />
      <div className="mb-8">
        <h1 className="dv-display text-3xl md:text-5xl mb-2">Marketplace</h1>
        <p className="text-slate-500 text-lg">{items.length} verified opportunities</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 md:p-4 mb-6 dv-shadow-soft sticky top-[72px] z-30">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5">
          <div className="md:col-span-5 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search businesses..." className="dv-input pl-11" data-testid="search-input" />
          </div>
          <select value={industry} onChange={e => setIndustry(e.target.value)} className="dv-input md:col-span-3" data-testid="filter-industry">
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
          <select value={country} onChange={e => setCountry(e.target.value)} className="dv-input md:col-span-2" data-testid="filter-country">
            {COUNTRIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select value={type} onChange={e => setType(e.target.value)} className="dv-input md:col-span-2" data-testid="filter-type">
            {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading opportunities...</div>
      ) : items.length === 0 ? (
        <div className="bg-slate-50 rounded-2xl py-20 text-center text-slate-400" data-testid="empty-state">No opportunities match your filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="listings-grid">
          {items.map(l => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
}
